import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChefHat, Minus, PauseCircle, Percent, Plus, Trash2, UserRound, XCircle } from 'lucide-react';
import { ORDER_SOURCES, ORDER_TYPES } from '@shripos/shared/constants';
import { rateLabel } from '@shripos/shared/billing';
import { Button, Input, Select, cn } from '@shripos/ui';
import { FoodTypeMark } from '../../components/FoodTypeMark.jsx';
import { rupees } from '../../lib/money.js';
import { useCart } from '../../stores/cart.js';

function OrderBar({ catalog, onCustomer }) {
  const { t } = useTranslation();
  const cart = useCart((s) => s.cart);
  const patch = useCart((s) => s.patch);
  const types = ORDER_TYPES.filter((o) => catalog.settings.orderTypes[o.key]);
  const sources = ORDER_SOURCES.filter((o) => catalog.settings.sources[o.key]);
  return (
    <div className="space-y-2 border-b border-slate-200 p-3">
      <div
        className="flex gap-1 rounded-xl bg-slate-100 p-1"
        role="radiogroup"
        aria-label={t('pos.orderType', 'Order type')}
      >
        {types.map((o) => (
          <button
            key={o.key}
            role="radio"
            aria-checked={cart.orderType === o.key}
            onClick={() => patch({ orderType: o.key })}
            className={cn(
              'flex-1 rounded-lg py-2 text-sm font-semibold',
              cart.orderType === o.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600',
            )}
          >
            {t(`pos.type.${o.key}`, o.label)}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        {sources.length > 1 && (
          <Select
            value={cart.source}
            onChange={(e) => patch({ source: e.target.value })}
            className="w-32 shrink-0"
            aria-label={t('pos.orderSource', 'Order source')}
          >
            {sources.map((s) => (
              <option key={s.key} value={s.key}>
                {t(`pos.source.${s.key}`, s.label)}
              </option>
            ))}
          </Select>
        )}
        {cart.orderType === 'dine_in' && (
          <Input
            value={cart.tableNo}
            onChange={(e) => patch({ tableNo: e.target.value.slice(0, 12) })}
            placeholder={t('pos.table', 'Table')}
            className="w-24 shrink-0"
            aria-label={t('pos.tableNumber', 'Table number')}
          />
        )}
        <button
          onClick={onCustomer}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-3 text-left text-sm ring-1 ring-slate-300 hover:bg-slate-50"
        >
          <UserRound className="size-4 shrink-0 text-slate-400" />
          <span className="truncate">
            {cart.customer ? cart.customer.name || cart.customer.phone : t('pos.addCustomer', 'Customer')}
          </span>
        </button>
      </div>
    </div>
  );
}

export function CartPanel({
  catalog,
  priced,
  onCustomer,
  onLine,
  onDiscount,
  onHold,
  onKot,
  onPay,
  onCancelOrder,
  busy,
}) {
  const { t } = useTranslation();
  const cart = useCart((s) => s.cart);
  const setQty = useCart((s) => s.setQty);
  const reset = useCart((s) => s.reset);
  const totals = priced?.totals;
  const empty = cart.lines.length === 0;
  const hasErrors = Object.keys(priced?.errors ?? {}).length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <OrderBar catalog={catalog} onCustomer={onCustomer} />
      {cart.billId && (
        <div className="flex items-center justify-between bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
          <span>
            {t('pos.editingOrder', 'Open order')}
            {cart.tokenNo ? ` · ${t('pos.token', 'Token')} ${cart.tokenNo}` : ''}
          </span>
          <button onClick={onCancelOrder} className="inline-flex items-center gap-1 font-semibold hover:underline">
            <XCircle className="size-3.5" /> {t('pos.cancelOrder', 'Cancel order')}
          </button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="cart-lines">
        {empty ? (
          <p className="p-6 text-center text-sm text-slate-500">
            {t('pos.emptyCart', 'Tap items to add them to the order.')}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {cart.lines.map((l) => {
              const p = priced?.byId.get(l.lineId);
              const error = priced?.errors[l.lineId];
              const sent = cart.sentQty?.[l.lineId] ?? 0;
              const name = p?.meta.name ?? t('pos.unavailableItem', 'Unavailable item');
              return (
                <li key={l.lineId} className={cn('flex gap-2 px-3 py-2.5', error && 'bg-red-50')}>
                  <button className="min-w-0 flex-1 text-left" onClick={() => onLine(l)}>
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                      {p && <FoodTypeMark type={p.meta.foodType} className="size-3" />}
                      <span className="truncate">{name}</span>
                      {p?.meta.variantName && (
                        <span className="font-normal text-slate-500">({p.meta.variantName})</span>
                      )}
                    </p>
                    {p?.meta.modifiers.length > 0 && (
                      <p className="truncate text-xs text-slate-500">
                        + {p.meta.modifiers.map((m) => m.name).join(', ')}
                      </p>
                    )}
                    {l.note && <p className="truncate text-xs text-amber-700 italic">“{l.note}”</p>}
                    {l.discount && (
                      <p className="text-xs text-emerald-700">
                        {t('pos.off', '{{amount}} off', {
                          amount:
                            l.discount.type === 'percent'
                              ? rateLabel(l.discount.valueBp)
                              : rupees(l.discount.valuePaise),
                        })}
                      </p>
                    )}
                    {sent > 0 && (
                      <p className="text-[11px] text-sky-700">
                        {t('pos.sentToKitchenQty', '{{count}} sent to kitchen', { count: sent })}
                      </p>
                    )}
                    {error && (
                      <p className="flex items-center gap-1 text-xs text-red-700">
                        <AlertTriangle className="size-3" /> {error}
                      </p>
                    )}
                  </button>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="text-sm font-semibold tabular-nums">
                      {p ? rupees(p.amounts.grossPaise - p.amounts.lineDiscountPaise) : '—'}
                    </span>
                    <div className="flex items-center rounded-lg ring-1 ring-slate-200">
                      <button
                        className="p-1.5"
                        onClick={() => setQty(l.lineId, l.qty - 1)}
                        aria-label={t('pos.decrease', 'Decrease {{name}}', { name })}
                      >
                        {l.qty === 1 ? <Trash2 className="size-3.5 text-red-600" /> : <Minus className="size-3.5" />}
                      </button>
                      <span className="w-7 text-center text-sm font-semibold tabular-nums">{l.qty}</span>
                      <button
                        className="p-1.5"
                        onClick={() => setQty(l.lineId, l.qty + 1)}
                        aria-label={t('pos.increase', 'Increase {{name}}', { name })}
                      >
                        <Plus className="size-3.5" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="border-t border-slate-200 p-3">
        {totals && !empty && (
          <dl className="mb-2 space-y-0.5 text-sm">
            <div className="flex justify-between text-slate-600">
              <dt>
                {t('pos.items', 'Items')} ({totals.itemCount})
              </dt>
              <dd className="tabular-nums">{rupees(totals.grossPaise)}</dd>
            </div>
            {totals.discountPaise > 0 && (
              <div className="flex justify-between text-emerald-700">
                <dt>{t('pos.discount', 'Discount')}</dt>
                <dd className="tabular-nums">−{rupees(totals.discountPaise)}</dd>
              </div>
            )}
            {priced.charges.map((c) => (
              <div key={c.key} className="flex justify-between text-slate-600">
                <dt>{c.label}</dt>
                <dd className="tabular-nums">{rupees(c.amountPaise)}</dd>
              </div>
            ))}
            {totals.igstPaise > 0 ? (
              <div className="flex justify-between text-slate-500">
                <dt>IGST</dt>
                <dd className="tabular-nums">{rupees(totals.igstPaise)}</dd>
              </div>
            ) : (
              <div className="flex justify-between text-slate-500">
                <dt>
                  CGST + SGST{' '}
                  {catalog.items.some((i) => i.priceMode === 'inclusive') ? t('pos.inclusive', '(incl.)') : ''}
                </dt>
                <dd className="tabular-nums">{rupees(totals.cgstPaise + totals.sgstPaise)}</dd>
              </div>
            )}
            {totals.roundOffPaise !== 0 && (
              <div className="flex justify-between text-slate-500">
                <dt>{t('pos.roundOff', 'Round off')}</dt>
                <dd className="tabular-nums">{rupees(totals.roundOffPaise)}</dd>
              </div>
            )}
          </dl>
        )}
        {/* No KOT button when this outlet has no kitchen (onKot is null). */}
        <div className={cn('mb-2 grid gap-2', onKot ? 'grid-cols-4' : 'grid-cols-3')}>
          <Button variant="secondary" size="sm" icon={Percent} disabled={empty} onClick={onDiscount}>
            <span className="hidden lg:inline">{t('pos.offers', 'Offers')}</span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={PauseCircle}
            disabled={empty || busy}
            onClick={onHold}
            title={t('pos.holdTitle', 'Hold (F6)')}
          >
            <span className="hidden lg:inline">{t('pos.hold', 'Hold')}</span>
          </Button>
          {onKot && (
            <Button
              variant="secondary"
              size="sm"
              icon={ChefHat}
              disabled={empty || busy || hasErrors}
              onClick={onKot}
              title={t('pos.kotTitle', 'Send to kitchen (F8)')}
            >
              {t('pos.kot', 'KOT')}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            icon={Trash2}
            disabled={empty || busy}
            onClick={() => (cart.billId ? onCancelOrder() : reset())}
            title={t('pos.clear', 'Clear')}
            aria-label={t('pos.clearOrder', 'Clear order')}
          />
        </div>
        <Button
          size="xl"
          className="w-full justify-between"
          disabled={empty || busy || hasErrors || !totals}
          onClick={onPay}
          data-testid="pay-button"
        >
          <span>{t('pos.pay', 'Pay')}</span>
          <span className="tabular-nums">{totals ? rupees(totals.grandTotalPaise) : ''}</span>
        </Button>
      </div>
    </div>
  );
}
